<template>
  <LazyUiLoader v-if="status === 'pending'" />
  <div
    v-else-if="
      data?.data?.userFullInventory
        ? Object.keys(data?.data?.userFullInventory?.assets).length > 0
        : false
    "
    class="inventory-list"
  >
    <div class="inventory-filters">
      <UiInput
        v-model="searchQuery"
        :placeholder="t('search')"
        class="w100"
      />

      <button
        :class="{ asc: sortBy === 'price_asc' }"
        class="btn btn-sort"
        type="button"
        @click="sortBy = sortBy === 'price_desc' ? 'price_asc' : 'price_desc'"
      >
        <span class="btn-sort__wrapper">
          <span class="btn-sort__line btn-sort__line_1" />
          <span class="btn-sort__line btn-sort__line_2" />
          <span class="btn-sort__line btn-sort__line_3" />
        </span>

        <span class="btn-sort__label">{{ t("sort_by_price") }}</span>
      </button>

      <MarketsDD
        v-model="market"
        :items="markets"
        class="inventory-filters__market"
        label-key="market_name"
      />

      <!--      <UiSelect -->
      <!--          v-model="sortBy" -->
      <!--          :items="sortByOptions" -->
      <!--          label-key="label" -->
      <!--          value-key="value" -->
      <!--      > -->
      <!--        <template #top-in> -->
      <!--          {{ -->
      <!--            t('filters.sort.sortBy') -->
      <!--          }}: {{ t(sortByOptions.find((el) => el.value === sortBy)?.label)?.toLowerCase() }} -->
      <!--        </template> -->
      <!--        <template #default-in="{ item }"> -->
      <!--          <span class="f"> -->
      <!--            <span>{{ t(item.label) }}</span> -->
      <!--          </span> -->
      <!--        </template> -->
      <!--      </UiSelect> -->
      <!--      </div> -->
      <!--    </div> -->

      <UiMultiSelect
        v-model:model="selectedCategories"
        :items="categoryOptions.map((opt) => opt.value)"
        :placeholder="t('categories_label')"
        clearable
      >
        <template #top-in="{ items: slotItems }">
          {{
            slotItems?.length
              ? slotItems
                .map((item) => t(categoryOptions.find((opt) => opt.value === item)?.label || ""))
                .join(", ")
              : t("all_categories")
          }}
        </template>
        <template #default-in="{ item }">
          <span class="f">
            <span>{{ t(categoryOptions.find((i) => i.value === item)?.label || "") }}</span>
          </span>
        </template>
      </UiMultiSelect>
    </div>

    <div class="inventory-list_wrap">
      <div
        v-if="paginatedItems.length"
        class="inventory-list__inner"
      >
        <div class="list">
          <PagesInventoryInnerInventoryItem
            v-for="(item, index) in paginatedItems"
            :key="item.asset_id"
            :item="item"
            :loading="index < 10 ? 'eager' : 'lazy'"
            :market="market?.market_name"
            @click="selectedItem = item"
          />
        </div>

        <LazyPaginateV3
          v-if="maxPages > 1"
          v-model="page"
          :pages-length="maxPages"
          :total="totalItems"
          :total-items="limit * page > totalItems ? totalItems : limit * page"
        />

        <LazyModalsModalWithSlot :modal-id="ModalIds.INVENTORY_ITEM">
          <LazyPagesInventoryInnerItemModal
            v-if="selectedItem"
            :item="selectedItem"
            :market="market?.market_name"
          />
        </LazyModalsModalWithSlot>
      </div>
      <div
        v-else-if="status === 'success'"
        class="inventory-list_empty"
      >
        <img
          alt=""
          src="/img/tradeup-empty.svg"
        >
        <p>{{ t("no_items_found") }}</p>
      </div>
    </div>
  </div>
  <NoItems
    v-else-if="status === 'success'"
    @update-inventory="refresh"
  />
</template>

<script lang="ts" setup>
import type { AvailableMarkets } from '~/graphql/generated/schema-types';
import type { InventoryItem } from '~/types/InventoryItem';
import MarketsDD from '~/components/Pages/Inventories/MarketsDD.vue';
import NoItems from '~/components/Pages/inventory/Inner/NoItems.vue';
import { EventBus } from '~/composables/bus';
import { useModalStore } from '~/store/modal';
import { ModalIds } from '~/types/Modals';

const props = defineProps<{
  steam64: string
}>();
const { t } = useI18n();
const modalStore = useModalStore();
const sortBy = ref<'price_desc' | 'price_asc'>('price_desc');
const limit = ref(12);
const page = ref(1);
const maxPages = ref(0);
const totalItems = ref(0);
const market = ref();
const selectedCategories = ref<string[]>([]);
const searchQuery = ref('');
const selectedItem = ref<null | InventoryItem>(null);

const { data: filters } = await useAsyncData('getComparisonFilters', getComparisonFilters);

const markets = computed<AvailableMarkets[]>(
  () => (filters.value?.data.comparePageFilters.markets || []) as AvailableMarkets[],
);

watchEffect(() => {
  if (markets.value.length > 0 && !market.value) {
    market.value = markets.value.find(el => el.market_name === 'steamcommunity');
  }
});

const { data, error, refresh, status } = await useAsyncData('full-inventory', () =>
  getUserFullInventory({
    market: market.value?.market_name,
    steam64: props.steam64,
  }));

function parseStickerInfo(stickerPriceInfo: (string | number | null)[] | undefined) {
  if (!stickerPriceInfo)
    return {};

  return {
    price: stickerPriceInfo[2] as number,
    rarity: stickerPriceInfo[5] as string | null,
    rarity_color: stickerPriceInfo[6] as string | null,
  };
}

const items = computed(() => {
  if (!data.value?.data?.userFullInventory)
    return [];

  const { assets, price_data } = data.value.data.userFullInventory;

  const allItems = Object.entries(assets).flatMap(([market_hash_name, assetsList]) => {
    const priceInfo = price_data[market_hash_name];

    return (assetsList as [string, string[]][]).map((assetData) => {
      const [asset_id, stickers] = assetData;

      const is_stattrak = market_hash_name.includes('StatTrak™');
      const is_souvenir = market_hash_name.includes('Souvenir');

      const stickerDetails = stickers?.map(stickerName => ({
        name: stickerName,
        ...parseStickerInfo(price_data[stickerName]),
      }));
      return {
        asset_id,
        market_hash_name,
        localed_name: priceInfo[0] as string,
        exterior: priceInfo[1] as string | null,
        price: priceInfo[2] as number | null,
        price_difference: (priceInfo[3] as number | null) ?? 0,
        is_stattrak,
        is_souvenir,
        stickers: stickerDetails || [],
        phase: null,
        skin: null,
        last_update: null,
        category: priceInfo[4] as string,
        rarity: priceInfo[5] as string | null,
        rarity_color: priceInfo[6] as string | null,
        collection: priceInfo[7] as string | null,
      };
    });
  });
  return allItems.filter(
    (item, index, self) => index === self.findIndex(t => t.asset_id === item.asset_id),
  );
});

const mainCategories = ['Stickers', 'Knives', 'Gloves', 'Rifles', 'Pistols', 'SMGs', 'Heavy'];

const categoryOptions = [
  { label: 'filters.weapon.Stickers', value: 'Stickers' },
  { label: 'categories.Knives', value: 'Knives' },
  { label: 'categories.Gloves', value: 'Gloves' },
  { label: 'categories.Rifles', value: 'Rifles' },
  { label: 'categories.Pistols', value: 'Pistols' },
  { label: 'categories.SMGs', value: 'SMGs' },
  { label: 'categories.Heavy', value: 'Heavy' },
  { label: 'categories.Other', value: 'Other' },
];

watch([selectedCategories, sortBy, searchQuery], () => {
  page.value = 1;
});

const filteredItems = computed(() => {
  let result = [...items.value];

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      item =>
        item.market_hash_name.toLowerCase().includes(query)
        || item.localed_name.toLowerCase().includes(query),
    );
  }

  if (selectedCategories.value.length > 0) {
    result = result.filter((item) => {
      if (selectedCategories.value.includes('Other')) {
        return (
          selectedCategories.value.includes(item.category)
          || !mainCategories.includes(item.category)
        );
      }
      return selectedCategories.value.includes(item.category);
    });
  }

  result.sort((a, b) => {
    const priceA = a.price || 0;
    const priceB = b.price || 0;

    if (sortBy.value === 'price_desc') {
      return priceB - priceA;
    } else {
      return priceA - priceB;
    }
  });

  return result;
});

const paginatedItems = ref<InventoryItem[]>([]);

watchEffect(() => {
  const result = filteredItems.value;

  totalItems.value = result.length;
  maxPages.value = Math.ceil(result.length / limit.value);

  if (page.value > maxPages.value) {
    page.value = Math.max(1, maxPages.value);
  }

  const start = (page.value - 1) * limit.value;
  const end = start + limit.value;

  paginatedItems.value = result.slice(start, end) as InventoryItem[];
});

if (data.value?.errors?.length || error.value) {
  EventBus.$emit('notify', {
    success: false,
    msg: data.value?.errors?.[0]?.message || error.value?.message || 'An error occurred',
  });
  console.log('getUserFullInventory() error: ', data.value?.errors?.length || error.value?.message);
}

watch([market], () => {
  page.value = 1;
  refresh();
});

watch(selectedItem, (newValue) => {
  if (newValue) {
    modalStore.openModal(
      ModalIds.INVENTORY_ITEM,
      { item: newValue },
      {
        onClose: () => {
          selectedItem.value = null;
        },
      },
    );
  }
});

// eslint-disable-next-line vue/no-expose-after-await
defineExpose({
  market,
  total: items,
});
</script>

<style lang="scss" scoped>
.inventory-list {
  .inventory-filters {
    margin-bottom: 20px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 16px;
    @media screen and (max-width: 768px) {
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    & > * {
      max-width: 100%;
      width: 100%;
      min-width: 100%;
    }

    &__market {
      min-width: 170px;
      @media screen and (max-width: 768px) {
        min-width: 150px;
      }
    }
  }

  .btn-sort {
    display: flex;
    justify-content: flex-start;
    gap: 6px;
    border: 1px solid #252930;

    &:hover {
      background: color-mix(in srgb, var(--surfaces-low-container) 96%, white);
    }

    &__wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      min-width: 18px;
      gap: 3px;
    }

    &__line {
      display: inline-block;
      height: 1.5px;
      border-radius: 2px;
      background: var(--accent-on-surface);
      transition: width 0.3s ease;

      &_1 {
        width: 18px;
      }

      &_2 {
        width: 14px;
      }

      &_3 {
        width: 10px;
      }
    }

    &__label {
      color: var(--surfaces-on-surface);
      font-size: 14px;
      font-weight: 600;
      line-height: normal;
      white-space: nowrap;
    }

    &.asc {
      .btn-sort__line {
        &_1 {
          width: 10px;
        }

        &_2 {
          width: 14px;
        }

        &_3 {
          width: 18px;
        }
      }
    }
  }

  .list {
    margin-bottom: 8px;
    color: var(--surfaces-on-surface);
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    @media screen and (max-width: 768px) {
      gap: 8px;
    }

    .item_wrap {
      max-width: initial;
      width: 100%;
    }
  }

  &_wrap {
    display: flex;
    flex-direction: column;
  }

  &__inner {
    //min-height: 853px;
    display: flex;
    flex-direction: column;

    .paginate {
      margin-top: auto;
    }
  }

  &_empty {
    display: flex;
    flex-direction: column;
    gap: 32px;
    align-items: center;
    justify-content: center;
    padding: 100px 0;

    img {
      width: 200px;
    }
  }
}
</style>
